import type {
  BranchChangeset,
  ConfigurationOperation,
  ConfigurationPackage,
  MergeConflict,
} from '@festina/configuration-general-types/configuration'
import { type BranchKey, LayerKey } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'

import { userActionsForOperation } from '../helpers'
import type { OperationWithSourceInfo } from '../types'

export const isEqualBranch = (
  key1: Optional<BranchKey>,
  key2: Optional<BranchKey>,
) => {
  if (!key1 || !key2) {
    return key1 == key2
  }

  return (
    (key1.name ?? undefined) === (key2.name ?? undefined) &&
    key1.server === key2.server
  )
}

export const filterConfigurationPackageOperations = <
  T extends ConfigurationOperation,
>(
  configurationPackage: Optional<ConfigurationPackage>,
  assertType: (operation: Optional<ConfigurationOperation>) => operation is T,
): Optional<ConfigurationPackage> =>
  configurationPackage
    ? {
        ...configurationPackage,
        changesets: configurationPackage.changesets
          ?.filter(notEmpty)
          .map((changeset) => ({
            ...changeset,
            operations: changeset.operations?.filter(assertType),
          }))
          .filter(notEmpty),
      }
    : configurationPackage

export const extractConfigurationOperationsMergeConflicts = (
  conflicts: Optional<MergeConflict[]>,
  rootBlock: string,
) =>
  (conflicts ?? []).reduce<{
    leftHandSide: OperationWithSourceInfo<ConfigurationOperation>[]
    rightHandSide: OperationWithSourceInfo<ConfigurationOperation>[]
  }>(
    (acc, conflict) => {
      const leftHandSideOperations = conflict.lhsChangesets?.flatMap(
        (branchChangeset) =>
          extractOperationsWithSourceInfoFromBranchChangeset(
            branchChangeset,
            rootBlock,
          ),
      )
      const rightHandSideOperations = conflict.rhsChangesets?.flatMap(
        (branchChangeset) =>
          extractOperationsWithSourceInfoFromBranchChangeset(
            branchChangeset,
            rootBlock,
          ),
      )

      return {
        leftHandSide: [...acc.leftHandSide, ...(leftHandSideOperations ?? [])],
        rightHandSide: [
          ...acc.rightHandSide,
          ...(rightHandSideOperations ?? []),
        ],
      }
    },
    {
      leftHandSide: [],
      rightHandSide: [],
    },
  )

const extractOperationsWithSourceInfoFromBranchChangeset = (
  branchChangeset: Optional<BranchChangeset>,
  rootBlock: string,
) =>
  (branchChangeset?.changeset?.operations?.filter(notEmpty) ?? [])
    .map(
      (operation) =>
        ({
          internalOperation: operation,
          layer: new LayerKey({
            tenantId: branchChangeset?.changeset?.tenantId,
            layerId: branchChangeset?.changeset?.layer,
          }),
          block: branchChangeset?.block ?? rootBlock,
          isDraft: false,
          actions: userActionsForOperation(
            operation,
            branchChangeset?.changesetHistory,
          ),
          changesetHistory: branchChangeset?.changesetHistory,
          annulOperations: [],
        }) satisfies OperationWithSourceInfo<ConfigurationOperation>,
    )
    .filter(notEmpty)
